import React from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Navbar from "@/components/Navbar";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import RegisterChoice from "@/pages/RegisterChoice";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ChoosePath from "@/pages/ChoosePath";
import MyVault from "@/pages/MyVault";
import RegisterCompany from "@/pages/RegisterCompany";
import Roster from "@/pages/Roster";
import JobsList from "@/pages/JobsList";
import JobDetail from "@/pages/JobDetail";
import Import from "@/pages/Import";
import Forms from "@/pages/Forms";
import Settings from "@/pages/Settings";
import Subscription from "@/pages/Subscription";

// Owner console
import OwnerLayout from "@/components/OwnerLayout";
import OwnerDashboard from "@/pages/owner/OwnerDashboard";
import OwnerUsers from "@/pages/owner/OwnerUsers";
import OwnerCompanyUsers from "@/pages/owner/OwnerCompanyUsers";
import OwnerCertifications from "@/pages/owner/OwnerCertifications";
import OwnerLogs from "@/pages/owner/OwnerLogs";
import OwnerSettings from "@/pages/owner/OwnerSettings";
import OwnerForms from "@/pages/owner/OwnerForms";
import OwnerSubscriptions from "@/pages/owner/OwnerSubscriptions";

// Public legal pages
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";

// Stable prop references — hoisted so route elements don't re-create arrays on every render.
const ROLES_ADMIN_SUPERVISOR = ["admin", "supervisor"];
const ROLES_ADMIN_ONLY = ["admin"];
const TOASTER_OPTIONS = {
  className: "font-bold uppercase tracking-wider text-xs",
  style: { border: "2px solid currentColor", borderRadius: 0, fontFamily: "Inter, sans-serif" },
};

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-center text-muted-foreground font-bold uppercase tracking-wider">Checking session...</div>;
  if (!user) return <Navigate to="/" replace />;
  if (roles && !roles.includes(user.role)) {
    // Owners landing on standard routes go to /owner
    if (user.role === "owner") return <Navigate to="/owner" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function OwnerProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-center text-muted-foreground font-bold uppercase tracking-wider">Checking session...</div>;
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== "owner") return <Navigate to="/dashboard" replace />;
  return <OwnerLayout>{children}</OwnerLayout>;
}

function Dashboard() {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === "owner") return <Navigate to="/owner" replace />;
  if (!user.company_id) return <Navigate to="/vault" replace />;
  if (user.role === "employee") return <Navigate to="/vault" replace />;
  return <Roster />;
}

function Shell() {
  const { user } = useAuth();
  const loc = useLocation();
  const isOwnerRoute = loc.pathname.startsWith("/owner");
  // Suppress standard Navbar on owner routes (owner has its own sidebar)
  const showNavbar = user && !isOwnerRoute && user.role !== "owner";

  return (
    <>
      {showNavbar && <Navbar />}
      <Routes>
        <Route path="/" element={
          user ? <Navigate to={user.role === "owner" ? "/owner" : "/dashboard"} replace /> : <Login />
        } />
        <Route path="/signup" element={
          user ? <Navigate to={user.role === "owner" ? "/owner" : "/dashboard"} replace /> : <RegisterChoice />
        } />

        {/* Public legal pages — accessible without authentication */}
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms"   element={<Terms   />} />
        <Route path="/forgot-password" element={
          user ? <Navigate to={user.role === "owner" ? "/owner" : "/dashboard"} replace /> : <ForgotPassword />
        } />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/signup/user" element={
          user ? <Navigate to={user.role === "owner" ? "/owner" : "/dashboard"} replace /> : <Signup />
        } />
        <Route path="/register-company" element={
          user ? <Navigate to={user.role === "owner" ? "/owner" : "/dashboard"} replace /> : <RegisterCompany />
        } />

        <Route path="/choose" element={<Protected><ChoosePath /></Protected>} />
        <Route path="/vault" element={<Protected><MyVault /></Protected>} />

        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
        <Route path="/jobs" element={
          <Protected roles={ROLES_ADMIN_SUPERVISOR}><JobsList /></Protected>
        } />
        <Route path="/jobs/:id" element={
          <Protected roles={ROLES_ADMIN_SUPERVISOR}><JobDetail /></Protected>
        } />
        <Route path="/import" element={
          <Protected roles={ROLES_ADMIN_ONLY}><Import /></Protected>
        } />

        <Route path="/forms" element={<Protected><Forms /></Protected>} />
        <Route path="/settings" element={<Protected><Settings /></Protected>} />
        <Route path="/subscription" element={
          <Protected roles={ROLES_ADMIN_ONLY}><Subscription /></Protected>
        } />

        {/* Owner Console */}
        <Route path="/owner"               element={<OwnerProtected><OwnerDashboard /></OwnerProtected>} />
        <Route path="/owner/users"         element={<OwnerProtected><OwnerUsers /></OwnerProtected>} />
        <Route path="/owner/companies/:companyId/users" element={<OwnerProtected><OwnerCompanyUsers /></OwnerProtected>} />
        <Route path="/owner/certifications" element={<OwnerProtected><OwnerCertifications /></OwnerProtected>} />
        <Route path="/owner/logs"          element={<OwnerProtected><OwnerLogs /></OwnerProtected>} />
        <Route path="/owner/settings"      element={<OwnerProtected><OwnerSettings /></OwnerProtected>} />
        <Route path="/owner/forms"         element={<OwnerProtected><OwnerForms /></OwnerProtected>} />
        <Route path="/owner/subscriptions" element={<OwnerProtected><OwnerSubscriptions /></OwnerProtected>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <div className="App bg-background text-foreground min-h-screen">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <Shell />
            <Toaster position="top-right" richColors toastOptions={TOASTER_OPTIONS} />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}
